import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import StoryRing from './StoryRing';
import StoryViewer from './StoryViewer';
import { trackHit } from '../utils/api';

// Helper: Check if a category is Kampanyalar (case-insensitive, trim whitespace)
const isKampanyalarCat = (cat) => {
    if (!cat) return false;
    const nameLower = (cat.name || '').toLowerCase().trim();
    const slugLower = (cat.slug || '').toLowerCase().trim();
    return nameLower === 'kampanyalar' || nameLower === 'kampanya' ||
           slugLower === 'kampanyalar' || slugLower === 'kampanya';
};

const isKampanyalarName = (name) => {
    if (!name) return false;
    const nameLower = name.toLowerCase().trim();
    return nameLower === 'kampanyalar' || nameLower === 'kampanya';
};

// Get full image URL helper function
const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    // If starts with /uploads, use relative path (same domain)
    if (imagePath.startsWith('/uploads/')) {
        return imagePath; // Relative path works in production (same domain)
    }
    
    return imagePath;
};

// Search Icon Component
const SearchIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

// Turkish locale-aware lowercase helper (handles İ -> i, I -> ı correctly)
const turkishLowerCase = (str) => {
    if (!str) return '';
    return str
        .replace(/İ/g, 'i')
        .replace(/I/g, 'ı')
        .replace(/Ş/g, 'ş')
        .replace(/Ğ/g, 'ğ')
        .replace(/Ü/g, 'ü')
        .replace(/Ö/g, 'ö')
        .replace(/Ç/g, 'ç')
        .toLowerCase();
};

// WhatsApp Icon Component
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
);

const MenuHeader = ({ 
    restaurant, 
    organization,
    settings = {},
    isSettingEnabled = () => true, 
    branches, 
    onBranchChange, 
    selectedBranchId, 
    categories,
    onProductClick,
    onCategoryClick,
    stories = [],
    viewedStoryIds = [],
    onStoryView,
    onStoryComplete,
    visitorId,
    onHomeClick
}) => {
    const [showBranches, setShowBranches] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [lastSearchLogId, setLastSearchLogId] = useState(null);
    const [showStoryViewer, setShowStoryViewer] = useState(false);
    
    // Ref for debouncing search log
    const searchLogTimeoutRef = useRef(null);
    const lastLoggedTermRef = useRef('');

    // Backend URL for API calls - Production'da relative path kullan
    const isProduction = import.meta.env.PROD || window.location.hostname !== 'localhost';
    const BACKEND_URL = import.meta.env.VITE_BACKEND_BASE_URL || (isProduction ? '' : 'http://localhost:3000');
    
    // Import trackHit function
    // trackHit is now imported from utils/api

    // Log search to analytics (debounced)
    const logSearchAnalytics = useCallback(async (term, results) => {
        // Skip if same term was just logged
        if (term === lastLoggedTermRef.current) return;
        
        // Skip very short terms
        if (!term || term.trim().length < 2) return;
        
        // Skip if organization_id is missing
        if (!organization?.id) {
            console.debug('Search log skipped: organization_id missing');
            return;
        }
        
        lastLoggedTermRef.current = term;
        
        // Determine search type
        const discountKeywords = ['indirim', 'indirimli', 'kampanya', 'kampanyalı', 'ucuz', 'fırsat', 'discount', 'sale'];
        const termLower = term.toLowerCase();
        const isDiscountSearch = discountKeywords.some(k => termLower.includes(k));
        
        try {
            const response = await fetch(`${BACKEND_URL}/api/search/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    organization_id: organization.id, // Use organization.id directly (already checked above)
                    branch_id: selectedBranchId || null,
                    search_term: term.trim(),
                    search_type: isDiscountSearch ? 'discount' : 'general',
                    result_count: (results?.categories?.length || 0) + (results?.products?.length || 0),
                    category_results: results?.categories?.length || 0,
                    product_results: results?.products?.length || 0,
                    session_id: sessionStorage.getItem('menu_session_id') || null
                })
            });
            
            const data = await response.json();
            if (data.success && data.data?.search_log_id) {
                setLastSearchLogId(data.data.search_log_id);
            } else if (!data.success) {
                console.debug('Search log failed:', data.message);
            }
        } catch (error) {
            // Silent fail - analytics shouldn't break the UI
            console.debug('Search log error:', error);
        }
    }, [organization?.id, selectedBranchId, BACKEND_URL]);

    // Log click on search result
    const logSearchClick = useCallback(async (resultId, resultType) => {
        if (!lastSearchLogId) return;
        
        try {
            await fetch(`${BACKEND_URL}/api/search/click`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    search_log_id: lastSearchLogId,
                    clicked_result_id: resultId,
                    clicked_result_type: resultType
                })
            });
        } catch (error) {
            console.debug('Search click log error:', error);
        }
    }, [lastSearchLogId, BACKEND_URL]);

    // Handle scroll for header shadow effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Lock body scroll when sidebar is open
    useEffect(() => {
        if (showSidebar || showSearch) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showSidebar, showSearch]);

    // Get logo from settings, restaurant, or organization (priority: settings > restaurant > organization)
    // Check if settings.restaurant_logo exists and is not empty string
    const settingsLogo = settings?.restaurant_logo && settings.restaurant_logo.trim() !== '' ? settings.restaurant_logo : null;
    const restaurantLogo = restaurant?.logo && restaurant.logo.trim() !== '' ? restaurant.logo : null;
    const organizationLogo = organization?.logo && organization.logo.trim() !== '' ? organization.logo : null;
    const logoSource = settingsLogo || restaurantLogo || organizationLogo;
    const logoUrl = getImageUrl(logoSource);
    const restaurantName = restaurant?.name || organization?.name || 'Menü';

    // Story calculations
    const hasUnviewedStories = useMemo(() => {
        if (!stories || stories.length === 0) return false;
        return stories.some(story => !viewedStoryIds.includes(story.id));
    }, [stories, viewedStoryIds]);

    const allStoriesViewed = useMemo(() => {
        if (!stories || stories.length === 0) return false;
        return stories.every(story => viewedStoryIds.includes(story.id));
    }, [stories, viewedStoryIds]);

    // Handle story click
    const handleStoryClick = useCallback(() => {
        if (stories && stories.length > 0) {
            setShowStoryViewer(true);
        }
    }, [stories]);

    // Handle story viewer close
    const handleStoryViewerClose = useCallback((navigation) => {
        setShowStoryViewer(false);
        // Handle navigation from story link if needed
        if (navigation && onCategoryClick) {
            // Could navigate to category/product based on navigation.type and navigation.target
        }
    }, [onCategoryClick]);
    
    // Debug logo (only log when logo source changes)
    const prevLogoSourceRef = useRef(null);
    useEffect(() => {
        if (logoSource !== prevLogoSourceRef.current) {
            prevLogoSourceRef.current = logoSource;
            if (logoSource) {
                console.log('🖼️ Logo loaded:', {
                    from_settings: !!settingsLogo,
                    from_restaurant: !!restaurantLogo,
                    from_organization: !!organizationLogo,
                    source: logoSource,
                    final_url: logoUrl
                });
            } else {
                console.warn('⚠️ No logo found - using initial:', restaurantName.charAt(0));
            }
        }
    }, [logoSource, logoUrl, settingsLogo, restaurantLogo, organizationLogo, restaurantName]);

    // WhatsApp link with preset message
    const whatsappNumber = restaurant?.whatsapp_number?.replace(/[^0-9]/g, '') || restaurant?.phone?.replace(/[^0-9]/g, '');
    const whatsappMessage = encodeURIComponent(`Merhaba, bilgi almak istiyorum!\n\n${window.location.href}`);
    const whatsappUrl = whatsappNumber ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}` : null;

    // Check if product is currently active (time/day based)
    const isProductActive = (product) => {
        // Check status
        if (product.status && product.status !== 'active') return false;
        
        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
        
        // Check available days if specified
        if (product.available_days) {
            try {
                const availableDays = typeof product.available_days === 'string' 
                    ? JSON.parse(product.available_days) 
                    : product.available_days;
                if (Array.isArray(availableDays) && availableDays.length > 0) {
                    if (!availableDays.includes(currentDay)) return false;
                }
            } catch (e) {}
        }
        
        // Check time range if specified
        if (product.start_time && product.end_time) {
            const startTime = product.start_time.slice(0, 5);
            const endTime = product.end_time.slice(0, 5);
            if (currentTime < startTime || currentTime > endTime) return false;
        }
        
        return true;
    };

    // Check if category is active
    const isCategoryActive = (category) => {
        if (category.status && category.status !== 'active') return false;
        return true;
    };

    // Check if product has active discount - use backend's is_discount_active for consistency
    const hasActiveDiscount = (product) => {
        // Use backend-calculated is_discount_active if available (most reliable)
        if (product.is_discount_active !== undefined) {
            return product.is_discount_active === true || product.is_discount_active === 1;
        }
        
        // Fallback: Check for Nox's Choice discount
        if (product.is_nox_discount || product.is_nox_choice) {
            return product.discount_price && parseFloat(product.discount_price) > 0;
        }
        
        // Fallback: Manual calculation if backend value not available
        if (!product.discount_price || parseFloat(product.discount_price) <= 0) return false;
        
        const now = new Date();
        const currentDay = now.getDay();
        const currentTime = now.toTimeString().slice(0, 5);
        
        // Check discount days
        if (product.discount_days) {
            try {
                const discountDays = typeof product.discount_days === 'string'
                    ? JSON.parse(product.discount_days)
                    : product.discount_days;
                if (Array.isArray(discountDays) && discountDays.length > 0) {
                    if (!discountDays.includes(currentDay)) return false;
                }
            } catch (e) {}
        }
        
        // Check discount time range
        if (product.discount_start_time && product.discount_end_time) {
            const startTime = product.discount_start_time.slice(0, 5);
            const endTime = product.discount_end_time.slice(0, 5);
            if (currentTime < startTime || currentTime > endTime) return false;
        }
        
        return true;
    };

    // First, identify products in Kampanyalar category (source of truth for discounts)
    const kampanyalarProductIds = useMemo(() => {
        if (!categories) return new Set();
        
        const kampanyalarCat = categories.find(c => isKampanyalarCat(c));
        
        if (!kampanyalarCat || !kampanyalarCat.products) return new Set();
        
        return new Set(kampanyalarCat.products.map(p => p.id));
    }, [categories]);

    // Get all active products from active categories (deduplicated)
    const allProducts = useMemo(() => {
        if (!categories) return [];
        
        const productMap = new Map(); // Use Map to deduplicate by product ID
        
        // First pass: collect all unique products with their discount status
        categories.forEach(cat => {
            // Only include products from active categories
            if (!isCategoryActive(cat)) return;
            
            // Check if this is Kampanyalar category
            const isKampanyalar = isKampanyalarCat(cat);
            
            if (cat.products && cat.products.length > 0) {
                cat.products.forEach(product => {
                    // Only include active products
                    if (!isProductActive(product)) return;
                    
                    // Check if product already exists
                    const existing = productMap.get(product.id);
                    
                    if (existing) {
                        // If this product is in Kampanyalar, mark it as discounted
                        if (isKampanyalar) {
                            existing.isDiscounted = true;
                            existing.inKampanyalar = true;
                        }
                        return;
                    }
                    
                    // Product is discounted if it's in Kampanyalar category
                    // (Backend adds products to Kampanyalar only when they have active discounts)
                    const isInKampanyalar = kampanyalarProductIds.has(product.id);
                    const isDiscounted = isInKampanyalar || isKampanyalar;
                    
                    // Use original category name (not Kampanyalar) for better UX
                    let categoryName = cat.name;
                    if (isKampanyalar && product.category_ids && product.category_ids.length > 0) {
                        // Try to find original category name
                        const originalCat = categories.find(c => 
                            product.category_ids.includes(c.id) && 
                            !isKampanyalarCat(c)
                        );
                        if (originalCat) {
                            categoryName = originalCat.name;
                        }
                    }
                    
                    // Add product with category info and discount status
                    productMap.set(product.id, {
                        ...product,
                        categoryName: categoryName,
                        categoryId: cat.id,
                        isDiscounted: isDiscounted,
                        inKampanyalar: isInKampanyalar || isKampanyalar
                    });
                });
            }
        });
        
        return Array.from(productMap.values());
    }, [categories, kampanyalarProductIds]);

    // Get product features as searchable text
    const getProductFeatures = (product) => {
        const features = [];
        
        if (product.is_vegetarian) features.push('vejetaryen', 'vegetarian', 'vejeteryan');
        if (product.is_vegan) features.push('vegan');
        if (product.is_gluten_free) features.push('glutensiz', 'gluten free', 'glütensiz');
        if (product.is_spicy) features.push('acı', 'spicy', 'baharatlı');
        if (product.is_new) features.push('yeni', 'new');
        if (product.is_popular) features.push('popüler', 'popular', 'favori');
        if (product.is_chef_recommended) features.push('şef önerisi', 'önerilen', 'chef');
        
        // Allergens
        if (product.allergens) {
            try {
                const allergens = typeof product.allergens === 'string'
                    ? JSON.parse(product.allergens)
                    : product.allergens;
                if (Array.isArray(allergens)) {
                    features.push(...allergens.map(a => a.toLowerCase()));
                }
            } catch (e) {}
        }
        
        return features.join(' ');
    };

    // Get discounted products directly from Kampanyalar category for consistency
    const kampanyalarProducts = useMemo(() => {
        return allProducts.filter(p => p.inKampanyalar);
    }, [allProducts]);

    // Smart search results with deep search (Turkish locale-aware)
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return { categories: [], products: [] };
        
        // Use Turkish-aware lowercase for proper İ/I handling
        const term = turkishLowerCase(searchQuery.trim());
        const terms = term.split(' ').filter(t => t.length > 0);
        
        // Special keywords for discounted products (all lowercase Turkish)
        const discountKeywords = ['indirim', 'indirimli', 'kampanya', 'kampanyalı', 'ucuz', 'fırsat', 'discount', 'sale'];
        const isSearchingDiscount = discountKeywords.some(keyword => term.includes(keyword));
        
        // Search active categories only
        const matchedCategories = (categories || []).filter(cat => {
            if (!isCategoryActive(cat)) return false;
            
            return turkishLowerCase(cat.name).includes(term) ||
                   turkishLowerCase(cat.description || '').includes(term);
        }).slice(0, 5);
        
        // Deep search in products
        const matchedProducts = allProducts.filter(product => {
            // If searching for discount, use Kampanyalar category products for exact match
            if (isSearchingDiscount) {
                // Check if this product is in Kampanyalar category (backend-determined)
                return kampanyalarProducts.some(kp => kp.id === product.id);
            }
            
            // Search in product name (Turkish-aware)
            if (turkishLowerCase(product.name).includes(term)) return true;
            
            // Search in description (Turkish-aware)
            if (turkishLowerCase(product.description || '').includes(term)) return true;
            
            // Search in features (vegetarian, vegan, gluten-free, etc.)
            const features = getProductFeatures(product);
            if (features.includes(term)) return true;
            
            // Search with multiple terms (AND logic)
            if (terms.length > 1) {
                const searchableText = turkishLowerCase([
                    product.name,
                    product.description || '',
                    product.categoryName,
                    features
                ].join(' '));
                
                return terms.every(t => searchableText.includes(t));
            }
            
            // Search in category name (Turkish-aware)
            if (turkishLowerCase(product.categoryName).includes(term)) return true;
            
            return false;
        }).slice(0, 15);
        
        return {
            categories: matchedCategories,
            products: matchedProducts
        };
    }, [searchQuery, categories, allProducts]);

    // Log search analytics with debounce
    useEffect(() => {
        // Clear previous timeout
        if (searchLogTimeoutRef.current) {
            clearTimeout(searchLogTimeoutRef.current);
        }

        // Don't log empty searches
        if (!searchQuery.trim() || searchQuery.trim().length < 2) {
            return;
        }

        // Debounce: wait 800ms after user stops typing
        searchLogTimeoutRef.current = setTimeout(() => {
            logSearchAnalytics(searchQuery.trim(), searchResults);
        }, 800);

        return () => {
            if (searchLogTimeoutRef.current) {
                clearTimeout(searchLogTimeoutRef.current);
            }
        };
    }, [searchQuery, searchResults, logSearchAnalytics]);

    // Social media links
    const socialLinks = [
        { 
            name: 'Facebook', 
            url: restaurant?.facebook_url, 
            bgColor: 'bg-blue-500',
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
            )
        },
        { 
            name: 'Instagram', 
            url: restaurant?.instagram_url, 
            bgColor: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
            )
        },
        { 
            name: 'Twitter/X', 
            url: restaurant?.twitter_url, 
            bgColor: 'bg-black',
            icon: (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
            )
        },
        { 
            name: 'WhatsApp', 
            url: whatsappUrl, 
            bgColor: 'bg-green-500',
            icon: <WhatsAppIcon className="w-5 h-5" />
        },
    ].filter(link => link.url);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Track contact click if scrolling to contact section
        if (sectionId === 'contact-section' && organization?.id) {
            trackHit(organization.id, 'contact', 0).catch(err => console.debug('Track contact click error:', err));
        }
        setShowSidebar(false);
    };

    const scrollToCategory = (categoryId) => {
        const element = document.getElementById(`category-${categoryId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setShowSidebar(false);
    };

    const handleSearchProductClick = (product) => {
        // Log the click for analytics
        logSearchClick(product.id, 'product');
        
        setShowSearch(false);
        setSearchQuery('');
        if (onProductClick) {
            onProductClick(product);
        }
    };

    const handleSearchCategoryClick = (category) => {
        // Log the click for analytics
        logSearchClick(category.id, 'category');
        
        setShowSearch(false);
        setSearchQuery('');
        if (onCategoryClick) {
            onCategoryClick(category);
        }
    };

    const hasSearchResults = searchResults.categories.length > 0 || searchResults.products.length > 0;

    return (
        <>
            <header 
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    isScrolled 
                        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
                        : 'bg-white shadow-sm'
                }`}
            >
                {/* Main Header */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-3 md:py-4">
                        {/* Logo and Name */}
                        <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                            {/* Logo with Story Ring */}
                            <div className="flex-shrink-0">
                                <StoryRing
                                    stories={stories}
                                    hasUnviewed={hasUnviewedStories}
                                    allViewed={allStoriesViewed}
                                    onClick={handleStoryClick}
                                    size="default"
                                >
                                    {logoUrl ? (
                                        <img 
                                            src={logoUrl} 
                                            alt={restaurantName}
                                            className="h-12 w-12 md:h-16 md:w-16 rounded-full object-cover shadow-md"
                                            onError={(e) => {
                                                console.error('Logo load error:', e.target.src);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-md">
                                            <span className="text-white text-lg md:text-xl font-bold">
                                                {restaurantName.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </StoryRing>
                            </div>

                            {/* Restaurant Info */}
                            <div className="min-w-0 flex-1">
                                <h1 className="text-base md:text-xl font-bold text-gray-900 truncate">
                                    {restaurantName}
                                </h1>
                                {restaurant?.description && (
                                    <p className="text-xs text-gray-500 truncate mt-0.5 line-clamp-1">
                                        {restaurant.description}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Right Section */}
                        <div className="flex items-center gap-2 md:gap-3">
                            {/* WhatsApp Button */}
                            {whatsappUrl && isSettingEnabled('enable_whatsapp', true) && (
                                <a 
                                    href={whatsappUrl}
                                    onClick={() => organization?.id && trackHit(organization.id, 'whatsapp', 0).catch(err => console.debug('Track WhatsApp click error:', err))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-10 h-10 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all shadow-md shadow-green-500/30"
                                    title="WhatsApp ile iletişime geç"
                                >
                                    <WhatsAppIcon className="w-5 h-5" />
                                </a>
                            )}

                            {/* Search Button */}
                            {isSettingEnabled('enable_search', true) && (
                                <button
                                    onClick={() => setShowSearch(true)}
                                    className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                                    aria-label="Ara"
                                >
                                    <SearchIcon className="w-5 h-5 text-gray-700" />
                                </button>
                            )}

                            {/* Phone - Desktop & Mobile */}
                            {restaurant?.phone && (
                                <a 
                                    href={`tel:${restaurant.phone}`}
                                    onClick={() => organization?.id && trackHit(organization.id, 'phone', 0).catch(err => console.debug('Track phone click error:', err))}
                                    className="flex items-center justify-center w-10 h-10 md:gap-2 md:px-3 md:w-auto bg-orange-500 text-white rounded-xl md:rounded-lg hover:bg-orange-600 transition-all text-sm font-medium"
                                    title="Telefon ile ara"
                                >
                                    <svg className="w-5 h-5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span className="hidden md:inline">Ara</span>
                                </a>
                            )}

                            {/* Branch Selector */}
                            {branches && branches.length > 0 && (
                                <div className="relative hidden sm:block">
                                    <button
                                        onClick={() => setShowBranches(!showBranches)}
                                        className="flex items-center gap-1.5 px-2.5 md:px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all text-sm font-medium"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="hidden sm:inline">
                                            {selectedBranchId 
                                                ? branches.find(b => b.id === selectedBranchId)?.name || 'Şube'
                                                : 'Şube'
                                            }
                                        </span>
                                        <svg className={`w-3 h-3 transition-transform ${showBranches ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {/* Branch Dropdown */}
                                    {showBranches && (
                                        <>
                                            <div 
                                                className="fixed inset-0 z-40" 
                                                onClick={() => setShowBranches(false)}
                                            />
                                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                                                <div className="p-2">
                                                    <button
                                                        onClick={() => {
                                                            onBranchChange(null);
                                                            setShowBranches(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-3 rounded-xl text-sm flex items-center gap-3 transition-colors ${
                                                            selectedBranchId === null 
                                                                ? 'bg-orange-50 text-orange-600' 
                                                                : 'hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                            selectedBranchId === null ? 'bg-orange-100' : 'bg-gray-100'
                                                        }`}>
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                            </svg>
                                                        </div>
                                                        <span className="font-medium">Tüm Şubeler</span>
                                                        {selectedBranchId === null && (
                                                            <svg className="w-5 h-5 ml-auto text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    
                                                    {branches.map(branch => (
                                                        <button
                                                            key={branch.id}
                                                            onClick={() => {
                                                                onBranchChange(branch.id);
                                                                setShowBranches(false);
                                                            }}
                                                            className={`w-full text-left px-4 py-3 rounded-xl text-sm flex items-center gap-3 transition-colors ${
                                                                selectedBranchId === branch.id 
                                                                    ? 'bg-orange-50 text-orange-600' 
                                                                    : 'hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                                selectedBranchId === branch.id ? 'bg-orange-100' : 'bg-gray-100'
                                                            }`}>
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <span className="font-medium block truncate">{branch.name}</span>
                                                                {branch.address && (
                                                                    <span className="text-xs text-gray-400 block truncate">{branch.address}</span>
                                                                )}
                                                            </div>
                                                            {selectedBranchId === branch.id && (
                                                                <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Hamburger Menu Button */}
                            <button
                                onClick={() => setShowSidebar(true)}
                                className="flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                                aria-label="Menüyü aç"
                            >
                                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Smart Search Modal */}
            {showSearch && isSettingEnabled('enable_search', true) && (
                <>
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity"
                        onClick={() => {
                            setShowSearch(false);
                            setSearchQuery('');
                        }}
                    />
                    <div className="fixed inset-x-0 top-0 bottom-0 z-[70] p-4 pt-6 pb-safe flex flex-col">
                        <div className="max-w-2xl mx-auto w-full flex flex-col h-full max-h-full">
                            {/* Search Input */}
                            <div className="relative flex-shrink-0">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Ürün veya kategori ara..."
                                    autoFocus
                                    className="w-full px-5 py-4 pl-14 text-lg bg-white rounded-2xl shadow-2xl border-0 focus:ring-4 focus:ring-orange-500/30 outline-none"
                                />
                                <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowSearch(false);
                                        setSearchQuery('');
                                    }}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Search Results - Scrollable */}
                            {searchQuery.trim() && (
                                <div className="mt-3 bg-white rounded-2xl shadow-2xl flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                                    {hasSearchResults ? (
                                        <div className="divide-y divide-gray-100">
                                            {/* Categories Results */}
                                            {searchResults.categories.length > 0 && (
                                                <div className="p-3">
                                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">
                                                        Kategoriler
                                                    </h3>
                                                    <div className="space-y-1">
                                                        {searchResults.categories.map(category => (
                                                            <button
                                                                key={`cat-${category.id}`}
                                                                onClick={() => handleSearchCategoryClick(category)}
                                                                className="w-full flex items-center gap-3 p-3 hover:bg-orange-50 rounded-xl transition-colors text-left group"
                                                            >
                                                                {category.image ? (
                                                                    <img 
                                                                        src={getImageUrl(category.image)} 
                                                                        alt={category.name}
                                                                        className="w-12 h-12 rounded-xl object-cover"
                                                                    />
                                                                ) : (
                                                                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                                                                        <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <span className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors block truncate">
                                                                        {category.name}
                                                                    </span>
                                                                    {category.description && (
                                                                        <span className="text-sm text-gray-500 block truncate">
                                                                            {category.description}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" />
                                                                    </svg>
                                                                    Kategori
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Products Results */}
                                            {searchResults.products.length > 0 && (
                                                <div className="p-3">
                                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">
                                                        Ürünler ({searchResults.products.length})
                                                    </h3>
                                                    <div className="space-y-1">
                                                        {searchResults.products.map(product => (
                                                            <button
                                                                key={`prod-${product.id}`}
                                                                onClick={() => handleSearchProductClick(product)}
                                                                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left group ${
                                                                    product.isDiscounted 
                                                                        ? 'bg-green-50 hover:bg-green-100 border border-green-200' 
                                                                        : 'hover:bg-orange-50'
                                                                }`}
                                                            >
                                                                {/* Product Image */}
                                                                <div className="relative flex-shrink-0">
                                                                    {product.images?.[0]?.image_url ? (
                                                                        <img 
                                                                            src={getImageUrl(product.images[0].image_url)} 
                                                                            alt={product.name}
                                                                            className="w-14 h-14 rounded-xl object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                                                                            <span className="text-2xl">🍽️</span>
                                                                        </div>
                                                                    )}
                                                                    {/* Discount Badge */}
                                                                    {product.isDiscounted && (
                                                                        <div className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                                                            %{Math.round((1 - parseFloat(product.discount_price) / parseFloat(product.price)) * 100)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Product Info */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-0.5">
                                                                        <span className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors truncate">
                                                                            {product.name}
                                                                        </span>
                                                                        {product.isDiscounted && (
                                                                            <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                                                                                İNDİRİM
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-sm text-gray-500 block truncate">
                                                                        {product.categoryName}
                                                                    </span>
                                                                    {/* Product Features */}
                                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                                        {product.is_vegetarian && (
                                                                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">🥬 Vejetaryen</span>
                                                                        )}
                                                                        {product.is_vegan && (
                                                                            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">🌱 Vegan</span>
                                                                        )}
                                                                        {product.is_gluten_free && (
                                                                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">🌾 Glutensiz</span>
                                                                        )}
                                                                        {product.is_spicy && (
                                                                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">🌶️ Acı</span>
                                                                        )}
                                                                        {product.is_new && (
                                                                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">✨ Yeni</span>
                                                                        )}
                                                                        {product.is_popular && (
                                                                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">⭐ Popüler</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Price */}
                                                                <div className="text-right flex-shrink-0">
                                                                    {product.isDiscounted ? (
                                                                        <div>
                                                                            <span className="text-gray-400 line-through text-xs block">
                                                                                {parseFloat(product.price).toFixed(2)} ₺
                                                                            </span>
                                                                            <span className="text-green-600 font-bold text-lg">
                                                                                {parseFloat(product.discount_price).toFixed(2)} ₺
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-orange-600 font-bold">
                                                                            {parseFloat(product.price).toFixed(2)} ₺
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="p-8 text-center">
                                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <p className="text-gray-500 text-lg">Sonuç bulunamadı</p>
                                            <p className="text-gray-400 text-sm mt-1">Farklı bir arama terimi deneyin</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Search Hints */}
                            {!searchQuery.trim() && (
                                <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                                    <p className="text-center text-white font-medium mb-3">
                                        Arama ipuçları
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-2">
                                        <button 
                                            onClick={() => setSearchQuery('indirimli')}
                                            className="text-xs bg-green-500/80 text-white px-3 py-1.5 rounded-full hover:bg-green-500 transition"
                                        >
                                            🏷️ İndirimli Ürünler
                                        </button>
                                        <button 
                                            onClick={() => setSearchQuery('vejetaryen')}
                                            className="text-xs bg-white/20 text-white px-3 py-1.5 rounded-full hover:bg-white/30 transition"
                                        >
                                            🥬 Vejetaryen
                                        </button>
                                        <button 
                                            onClick={() => setSearchQuery('vegan')}
                                            className="text-xs bg-white/20 text-white px-3 py-1.5 rounded-full hover:bg-white/30 transition"
                                        >
                                            🌱 Vegan
                                        </button>
                                        <button 
                                            onClick={() => setSearchQuery('glutensiz')}
                                            className="text-xs bg-white/20 text-white px-3 py-1.5 rounded-full hover:bg-white/30 transition"
                                        >
                                            🌾 Glutensiz
                                        </button>
                                        <button 
                                            onClick={() => setSearchQuery('acı')}
                                            className="text-xs bg-white/20 text-white px-3 py-1.5 rounded-full hover:bg-white/30 transition"
                                        >
                                            🌶️ Acı
                                        </button>
                                    </div>
                                    <p className="text-center text-white/60 text-xs mt-3">
                                        Ürün adı, açıklaması veya özellikleriyle arayabilirsiniz
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Sidebar Menu */}
            {showSidebar && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity"
                        onClick={() => setShowSidebar(false)}
                    />
                    
                    {/* Sidebar */}
                    <div 
                        className="fixed right-0 top-0 bottom-0 w-full max-w-sm shadow-2xl z-[70] transform transition-transform duration-300 overflow-hidden flex flex-col"
                        style={{ backgroundColor: 'var(--sidebar-bg-color, #ffffff)' }}
                    >
                        {/* Sidebar Header */}
                        <div 
                            className="p-6 text-white"
                            style={{ background: `linear-gradient(to right, var(--sidebar-header-start, #f97316), var(--sidebar-header-end, #dc2626))` }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold">Menü</h2>
                                <button
                                    onClick={() => setShowSidebar(false)}
                                    className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            {/* Restaurant Info in Sidebar */}
                            <div className="flex items-center gap-3">
                                {logoUrl ? (
                                    <div 
                                        className="h-16 w-16 rounded-xl flex items-center justify-center overflow-hidden"
                                        style={{ backgroundColor: 'var(--sidebar-logo-bg, #ffffff)' }}
                                    >
                                        <img 
                                            src={logoUrl} 
                                            alt={restaurantName}
                                            className="h-14 w-14 rounded-lg object-contain"
                                        />
                                    </div>
                                ) : (
                                    <div 
                                        className="h-16 w-16 rounded-xl flex items-center justify-center"
                                        style={{ backgroundColor: 'var(--sidebar-logo-bg, #ffffff)' }}
                                    >
                                        <span 
                                            className="text-2xl font-bold"
                                            style={{ color: 'var(--sidebar-header-start, #f97316)' }}
                                        >
                                            {restaurantName.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold text-lg">{restaurantName}</h3>
                                    {restaurant?.description && (
                                        <p className="text-white/80 text-sm line-clamp-2">{restaurant.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar Content */}
                        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: 'var(--sidebar-bg-color, #ffffff)' }}>
                            {/* Quick Navigation */}
                            <div className="p-4 border-b" style={{ borderColor: 'var(--category-border-color, #e5e7eb)', backgroundColor: 'var(--sidebar-bg-color, #ffffff)' }}>
                                <h4 
                                    className="text-xs font-semibold uppercase tracking-wider mb-3"
                                    style={{ color: 'var(--text-muted-color, #9ca3af)' }}
                                >
                                    Hızlı Erişim
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            setShowSidebar(false);
                                        }}
                                        className="flex items-center gap-2 p-3 rounded-xl transition-colors text-sm font-medium"
                                        style={{ 
                                            backgroundColor: 'var(--sidebar-hover-color, #f3f4f6)',
                                            color: 'var(--sidebar-text-color, #374151)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--sidebar-active-color, #fff7ed)';
                                            e.currentTarget.style.color = 'var(--sidebar-active-text-color, #ea580c)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--sidebar-hover-color, #f3f4f6)';
                                            e.currentTarget.style.color = 'var(--sidebar-text-color, #374151)';
                                        }}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                        </svg>
                                        Ana Sayfa
                                    </button>
                                    <button
                                        onClick={() => {
                                            // Ana sayfaya git ve kategoriler bölümüne scroll yap
                                            if (onHomeClick) {
                                                onHomeClick();
                                            } else {
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }
                                            setTimeout(() => {
                                                scrollToSection('categories-section');
                                            }, 100);
                                            setShowSidebar(false);
                                        }}
                                        className="flex items-center gap-2 p-3 rounded-xl transition-colors text-sm font-medium"
                                        style={{ 
                                            backgroundColor: 'var(--sidebar-hover-color, #f3f4f6)',
                                            color: 'var(--sidebar-text-color, #374151)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--sidebar-active-color, #fff7ed)';
                                            e.currentTarget.style.color = 'var(--sidebar-active-text-color, #ea580c)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'var(--sidebar-hover-color, #f3f4f6)';
                                            e.currentTarget.style.color = 'var(--sidebar-text-color, #374151)';
                                        }}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                        Kategoriler
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowSidebar(false);
                                            setShowSearch(true);
                                        }}
                                        className="flex items-center gap-2 p-3 rounded-xl transition-colors text-sm font-medium"
                                        style={{ 
                                            backgroundColor: 'var(--sidebar-active-color, #fff7ed)',
                                            color: 'var(--sidebar-active-text-color, #ea580c)'
                                        }}
                                    >
                                        <SearchIcon className="w-5 h-5" />
                                        Ara
                                    </button>
                                    {whatsappUrl && (
                                        <a
                                            href={whatsappUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 p-3 bg-green-50 hover:bg-green-100 rounded-xl transition-colors text-sm font-medium text-green-700"
                                            onClick={() => {
                                                if (organization?.id) {
                                                    trackHit(organization.id, 'whatsapp', 0).catch(err => console.debug('Track WhatsApp click error:', err));
                                                }
                                                setShowSidebar(false);
                                            }}
                                        >
                                            <WhatsAppIcon className="w-5 h-5" />
                                            WhatsApp
                                        </a>
                                    )}
                                    {restaurant?.phone && (
                                        <a
                                            href={`tel:${restaurant.phone}`}
                                            className="flex items-center gap-2 p-3 rounded-xl transition-colors text-sm font-medium"
                                            style={{ 
                                                backgroundColor: 'var(--sidebar-active-color, #fff7ed)',
                                                color: 'var(--sidebar-active-text-color, #ea580c)'
                                            }}
                                            onClick={() => {
                                                if (organization?.id) {
                                                    trackHit(organization.id, 'phone', 0).catch(err => console.debug('Track phone click error:', err));
                                                }
                                                setShowSidebar(false);
                                            }}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                            Hemen Ara
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Categories */}
                            {categories && categories.length > 0 && (
                                <div className="p-4 border-b" style={{ borderColor: 'var(--category-border-color, #e5e7eb)' }}>
                                    <h4 
                                        className="text-xs font-semibold uppercase tracking-wider mb-3"
                                        style={{ color: 'var(--text-muted-color, #9ca3af)' }}
                                    >
                                        Kategoriler
                                    </h4>
                                    <div className="space-y-1">
                                        {categories.filter(c => !c.parent_id).map(category => (
                                            <button
                                                key={category.id}
                                                onClick={() => {
                                                    setShowSidebar(false);
                                                    if (onCategoryClick) {
                                                        onCategoryClick(category);
                                                    } else {
                                                        scrollToCategory(category.id);
                                                    }
                                                }}
                                                className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left group"
                                                style={{ 
                                                    '--hover-bg': 'var(--sidebar-hover-color, #f3f4f6)',
                                                    '--active-text': 'var(--sidebar-active-text-color, #ea580c)'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sidebar-hover-color, #f3f4f6)'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                {category.image ? (
                                                    <img 
                                                        src={getImageUrl(category.image)} 
                                                        alt={category.name}
                                                        className="w-10 h-10 rounded-lg object-cover"
                                                    />
                                                ) : (
                                                    <div 
                                                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                                                        style={{ backgroundColor: 'var(--sidebar-active-color, #fff7ed)' }}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--primary-color, #f97316)' }}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <span 
                                                    className="font-medium transition-colors"
                                                    style={{ color: 'var(--sidebar-text-color, #374151)' }}
                                                >
                                                    {category.name}
                                                </span>
                                                <svg className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--text-muted-color, #9ca3af)' }}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Contact Info */}
                            {(restaurant?.address || restaurant?.email) && (
                                <div className="p-4 border-b" style={{ borderColor: 'var(--category-border-color, #e5e7eb)' }}>
                                    <h4 
                                        className="text-xs font-semibold uppercase tracking-wider mb-3"
                                        style={{ color: 'var(--text-muted-color, #9ca3af)' }}
                                    >
                                        İletişim Bilgileri
                                    </h4>
                                    <div className="space-y-3">
                                        {restaurant?.phone && (
                                            <a 
                                                href={`tel:${restaurant.phone}`}
                                                onClick={() => organization?.id && trackHit(organization.id, 'phone', 0).catch(err => console.debug('Track phone click error:', err))}
                                                className="flex items-center gap-3 text-sm transition-colors"
                                                style={{ color: 'var(--sidebar-text-color, #374151)' }}
                                            >
                                                <div 
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                                    style={{ backgroundColor: 'var(--sidebar-active-color, #fff7ed)' }}
                                                >
                                                    <svg 
                                                        className="w-5 h-5" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        viewBox="0 0 24 24"
                                                        style={{ color: 'var(--sidebar-active-text-color, #ea580c)' }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                </div>
                                                <span>{restaurant.phone}</span>
                                            </a>
                                        )}
                                        {restaurant?.email && (
                                            <a 
                                                href={`mailto:${restaurant.email}`}
                                                className="flex items-center gap-3 text-sm transition-colors"
                                                style={{ color: 'var(--sidebar-text-color, #374151)' }}
                                            >
                                                <div 
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                                    style={{ backgroundColor: 'var(--sidebar-active-color, #fff7ed)' }}
                                                >
                                                    <svg 
                                                        className="w-5 h-5" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        viewBox="0 0 24 24"
                                                        style={{ color: 'var(--sidebar-active-text-color, #ea580c)' }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <span>{restaurant.email}</span>
                                            </a>
                                        )}
                                        {restaurant?.address && (
                                            <div 
                                                className="flex items-start gap-3 text-sm"
                                                style={{ color: 'var(--sidebar-text-color, #374151)' }}
                                            >
                                                <div 
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                                    style={{ backgroundColor: 'var(--sidebar-active-color, #fff7ed)' }}
                                                >
                                                    <svg 
                                                        className="w-5 h-5" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        viewBox="0 0 24 24"
                                                        style={{ color: 'var(--sidebar-active-text-color, #ea580c)' }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                </div>
                                                <span>{restaurant.address}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Social Media */}
                            {socialLinks.length > 0 && (
                                <div className="p-4">
                                    <h4 
                                        className="text-xs font-semibold uppercase tracking-wider mb-3"
                                        style={{ color: 'var(--text-muted-color, #9ca3af)' }}
                                    >
                                        Sosyal Medya
                                    </h4>
                                    <div className="flex flex-wrap gap-3">
                                        {socialLinks.map((social, index) => (
                                            <a 
                                                key={index}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`${social.name} sayfamızı ziyaret edin`}
                                                className={`w-12 h-12 rounded-xl ${social.bgColor} flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}
                                                title={social.name}
                                            >
                                                {social.icon}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar Footer */}
                        <div 
                            className="p-4 border-t"
                            style={{ 
                                borderColor: 'var(--category-border-color, #e5e7eb)',
                                backgroundColor: 'var(--sidebar-hover-color, #f3f4f6)'
                            }}
                        >
                            <div className="flex flex-col items-center gap-1">
                                <p className="text-xs text-center" style={{ color: 'var(--text-muted-color, #9ca3af)' }}>
                                    Zehuma QR Menü © {new Date().getFullYear()}
                                </p>
                                <a 
                                    href="https://zehuma.com" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-center hover:opacity-80 transition"
                                    style={{ color: 'var(--text-muted-color, #9ca3af)' }}
                                >
                                    Zehuma Bilgi Teknolojileri A.Ş.
                                </a>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Story Viewer */}
            {showStoryViewer && stories.length > 0 && (
                <StoryViewer
                    stories={stories}
                    initialIndex={-1}
                    viewedStoryIds={viewedStoryIds}
                    onClose={handleStoryViewerClose}
                    onStoryView={onStoryView}
                    onStoryComplete={onStoryComplete}
                    organizationId={organization?.id}
                    visitorId={visitorId}
                />
            )}
        </>
    );
};

export default MenuHeader;
